// utils/submitGate.ts

export interface SubmitGate {
	/** Evaluates all registered conditions to see if submission should be allowed. */
	evaluate(): void;
}

interface MutableSubmitGate extends SubmitGate {
	addCondition(condition: () => boolean): void;
}

const gates = new WeakMap<HTMLButtonElement, MutableSubmitGate>();

/**
 * Creates a gate for a submit button, with no conditions registered yet.
 *
 * @param {HTMLButtonElement} button The submit button to gate
 * @returns {MutableSubmitGate} The new gate
 */
function createGate(button: HTMLButtonElement): MutableSubmitGate {
	const conditions: Array<() => boolean> = [];

	const gate: MutableSubmitGate = {
		addCondition(condition): void {
			conditions.push(condition);
			gate.evaluate();
		},

		evaluate(): void {
			button.disabled = !conditions.every((condition) => condition());
		},
	};

	return gate;
}

/**
 * Registers a condition against a submit button's shared gate, creating the gate on first use.
 *
 * @param {HTMLButtonElement} button The submit button to gate
 * @param {() => boolean} condition Returns true once this feature's requirement is satisfied
 * @returns {SubmitGate} The shared gate, to re-evaluate when this condition's answer changes
 */
export function registerSubmitGate(
	button: HTMLButtonElement,
	condition: () => boolean,
): SubmitGate {
	let gate = gates.get(button);

	if (!gate) {
		gate = createGate(button);
		gates.set(button, gate);
	}

	gate.addCondition(condition);

	return gate;
}
